import { motion } from "framer-motion";
import {
  Search,
  Layers,
  Target,
  Building2,
  MessageSquare,
  Flag,
  LucideIcon,
} from "lucide-react";

interface Etapa {
  numero: string;
  semanas: string;
  icon: LucideIcon;
  rielA: string;
  rielADetalle: string;
  rielB: string;
  gradient: string;
  destacado?: string;
}

const etapas: Etapa[] = [
  {
    numero: "01",
    semanas: "Semanas 1–2",
    icon: Search,
    rielA: "Escucha",
    rielADetalle: "Inmersión, entrevistas y diagnóstico.",
    rielB: "Liderazgo y visión.",
    gradient: "linear-gradient(135deg, #12315c, #1d4a8c)",
  },
  {
    numero: "02",
    semanas: "Semanas 3–4",
    icon: Layers,
    rielA: "Arqueología",
    rielADetalle: "Identidad profunda y mirada sobre la calle.",
    rielB: "Presencia y expresión.",
    gradient: "linear-gradient(135deg, #1d4a8c, #2f6fb0)",
  },
  {
    numero: "03",
    semanas: "Semanas 5–6",
    icon: Target,
    rielA: "Estrategia",
    rielADetalle: "Posición y territorio.",
    rielB: "Corporalidad aplicada.",
    gradient: "linear-gradient(135deg, #2f6fb0, #3B8BD4)",
    destacado: "Arranca el Piloto de Voz",
  },
  {
    numero: "04",
    semanas: "Semanas 7–8",
    icon: Building2,
    rielA: "Arquitectura",
    rielADetalle: "Sesión de trabajo con Tamara.",
    rielB: "Acompañamiento y práctica.",
    gradient: "linear-gradient(135deg, #3B8BD4, #2E9BA8)",
  },
  {
    numero: "05",
    semanas: "Semanas 9–10",
    icon: MessageSquare,
    rielA: "Sistema de Mensajes",
    rielADetalle: "Definición del equipo 360, OKRs y KPIs.",
    rielB: "Expresión y presencia del speaker.",
    gradient: "linear-gradient(135deg, #17836B, #1D9E75)",
  },
  {
    numero: "06",
    semanas: "Semanas 11–12",
    icon: Flag,
    rielA: "Consolidación",
    rielADetalle: "Entrega y hoja de ruta.",
    rielB: "Cierre y plan de sostenimiento.",
    gradient: "linear-gradient(135deg, #0F6E56, #1D9E75)",
  },
];

const ChiappellaProceso = () => {
  return (
    <section
      id="proceso"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, hsl(217 61% 10%), hsl(215 48% 18%))",
      }}
    >
      <div className="container px-4 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16 max-w-3xl mx-auto"
        >
          <span
            className="text-sm font-semibold tracking-[0.25em] uppercase mb-4 block"
            style={{ color: "#5BA3E0" }}
          >
            El Proceso
          </span>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1]">
            Noventa días,
            <br />
            <span style={{ color: "#5BA3E0" }}>
              un sistema que avanza con claridad.
            </span>
          </h2>
        </motion.div>

        {/* Etapas */}
        <div className="-mx-4 md:mx-0">
          <div className="flex md:grid md:grid-cols-3 lg:grid-cols-6 gap-5 overflow-x-auto md:overflow-visible px-4 md:px-0 pb-6 md:pb-0 snap-x snap-mandatory md:snap-none">
            {etapas.map((t, index) => {
              const Icon = t.icon;
              return (
                <motion.div
                  key={t.numero}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: index * 0.08 }}
                  className="flex-shrink-0 w-[78%] sm:w-[55%] md:w-auto snap-center rounded-2xl p-6 border border-white/10 flex flex-col"
                  style={{
                    background:
                      "linear-gradient(180deg, hsl(220 69% 4%), hsl(217 61% 8%))",
                  }}
                >
                  <div className="flex items-center justify-between mb-5">
                    <span
                      className="font-display text-2xl font-bold"
                      style={{ color: "#5BA3E0" }}
                    >
                      {t.numero}
                    </span>
                    <span className="text-[10px] tracking-[0.14em] uppercase font-semibold px-2.5 py-1 rounded-full border border-white/10 text-white/70 whitespace-nowrap">
                      {t.semanas}
                    </span>
                  </div>

                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{ background: t.gradient }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>

                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1">
                    Riel A · Marca
                  </p>
                  <h3 className="font-display text-lg font-semibold text-white mb-2 leading-snug">
                    {t.rielA}
                  </h3>
                  <p className="text-sm text-white/70 leading-relaxed mb-4">
                    {t.rielADetalle}
                  </p>

                  {t.destacado && (
                    <p
                      className="text-xs font-semibold mb-4 px-3 py-1.5 rounded-full inline-block self-start"
                      style={{
                        background: "rgba(29,158,117,0.15)",
                        color: "#4FD1A5",
                      }}
                    >
                      {t.destacado}
                    </p>
                  )}

                  <div className="mt-auto pt-4 border-t border-white/10">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1">
                      Riel B · CEO Visible
                    </p>
                    <p className="text-sm text-white/80 leading-relaxed">
                      {t.rielB}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center text-sm md:text-base text-white/65 mt-10"
        >
          El proyecto puede iniciarse dentro de los cinco días hábiles de
          aceptada la propuesta.
        </motion.p>
      </div>
    </section>
  );
};

export default ChiappellaProceso;
